import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import './AdvertiserForm.css';

interface Affiliate {
  id: number;
  name: string;
  email: string;
  apiUrl: string;
  country: string;
  postback: string;
  status: 'pending' | 'approved' | 'blocked';
  tier: 'standard' | 'premium' | 'sensitive';
  postbackPercentage: number;
  postbackStatus: 'start' | 'pause';
  emailVerified: 'YES' | 'NO';
  apiKey: string;
  timezone: string;
  dailyCap?: number;
  monthlyCap?: number;
  createdAt: string;
  lastLogin?: string;
  totalClicks?: number;
  totalConversions?: number;
  totalPayout?: number;
  conversionRate?: string;
  username?: string;
  password?: string;
}

const ViewAffiliate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [affiliate, setAffiliate] = useState<Affiliate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [allOffers, setAllOffers] = useState<any[]>([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState<string>('');
  const [customPayout, setCustomPayout] = useState<string>('');
  const [assignmentNotes, setAssignmentNotes] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const [publisherResponse, assignmentsResponse, offersResponse] = await Promise.all([
          apiService.getPublisher(parseInt(id)),
          apiService.getPublisherAssignments(parseInt(id)),
          apiService.getOffers()
        ]);

        const publisher = publisherResponse.publisher;
        setAffiliate({
          id: publisher.id,
          name: publisher.name || '',
          email: publisher.email || '',
          apiUrl: `https://track.adbizzmedia.com/api/publishers/${publisher.id}/offers`,
          country: publisher.country || '',
          postback: publisher.callback_template || '',
          status: publisher.status || 'pending',
          tier: publisher.tier || 'standard',
          postbackPercentage: publisher.postback_percentage ?? 100,
          postbackStatus: publisher.postback_status || 'start',
          emailVerified: 'YES',
          apiKey: publisher.api_key || '',
          timezone: publisher.timezone || '',
          dailyCap: publisher.daily_cap,
          monthlyCap: publisher.monthly_cap,
          createdAt: new Date(publisher.created_at).toLocaleDateString(),
          lastLogin: new Date(publisher.updated_at).toLocaleString(),
          totalClicks: publisher.total_clicks || 0,
          totalConversions: publisher.total_conversions || 0,
          totalPayout: parseFloat(publisher.total_payout) || 0,
          conversionRate: publisher.conversion_rate || '0.00',
          username: publisher.username || publisher.email,
          password: publisher.plain_password || '••••••••'
        });
        setAssignments(assignmentsResponse.assignments || []);
        setAllOffers(offersResponse.offers || []);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch publisher');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleBack = () => {
    navigate('/affiliates');
  };

  if (loading) {
    return (
      <div className="advertiser-view-page">
        <div className="loading">Loading affiliate details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="advertiser-view-page">
        <div className="error">Error: {error}</div>
        <button className="btn btn-secondary" onClick={handleBack}>
          Back to Affiliates
        </button>
      </div>
    );
  }

  if (!affiliate) {
    return (
      <div className="advertiser-view-page">
        <div className="error">Affiliate not found</div>
        <button className="btn btn-secondary" onClick={handleBack}>
          Back to Affiliates
        </button>
      </div>
    );
  }

  const handleEdit = () => {
    navigate(`/affiliates/edit/${id}`);
  };

  const handleRefresh = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [publisherResponse, assignmentsResponse] = await Promise.all([
        apiService.getPublisher(parseInt(id)),
        apiService.getPublisherAssignments(parseInt(id))
      ]);

      const publisher = publisherResponse.publisher;
      setAffiliate({
        id: publisher.id,
        name: publisher.name || '',
        email: publisher.email || '',
        apiUrl: `https://track.adbizzmedia.com/api/publishers/${publisher.id}/offers`,
        country: publisher.country || '',
        postback: publisher.callback_template || '',
        status: publisher.status || 'pending',
        tier: publisher.tier || 'standard',
        postbackPercentage: publisher.postback_percentage ?? 100,
        postbackStatus: publisher.postback_status || 'start',
        emailVerified: 'YES',
        apiKey: publisher.api_key || '',
        timezone: publisher.timezone || '',
        dailyCap: publisher.daily_cap,
        monthlyCap: publisher.monthly_cap,
        createdAt: new Date(publisher.created_at).toLocaleDateString(),
        lastLogin: new Date(publisher.updated_at).toLocaleString(),
        totalClicks: publisher.total_clicks || 0,
        totalConversions: publisher.total_conversions || 0,
        totalPayout: publisher.total_payout || 0,
        conversionRate: publisher.conversion_rate || '0.00'
      });
      setAssignments(assignmentsResponse.assignments || []);
    } catch (err: any) {
      setError(err.message || 'Failed to refresh affiliate data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignOffer = async () => {
    if (!id || !selectedOfferId) return;

    try {
      await apiService.assignPublisherToOffer(
        parseInt(selectedOfferId),
        parseInt(id),
        {
          status: 'approved',
          custom_payout: customPayout || undefined,
          notes: assignmentNotes || undefined
        }
      );

      // Refresh assignments
      const assignmentsResponse = await apiService.getPublisherAssignments(parseInt(id));
      setAssignments(assignmentsResponse.assignments || []);

      // Reset form
      setShowAssignModal(false);
      setSelectedOfferId('');
      setCustomPayout('');
      setAssignmentNotes('');
    } catch (err: any) {
      alert(err.message || 'Failed to assign offer');
    }
  };

  const handleRemoveOffer = async (offerId: number) => {
    if (!id || !window.confirm('Are you sure you want to remove this offer from the affiliate?')) return;

    try {
      await apiService.removePublisherFromOffer(offerId, parseInt(id));

      // Refresh assignments
      const assignmentsResponse = await apiService.getPublisherAssignments(parseInt(id));
      setAssignments(assignmentsResponse.assignments || []);
    } catch (err: any) {
      alert(err.message || 'Failed to remove offer');
    }
  };

  const getAvailableOffers = () => {
    const assignedOfferIds = new Set(assignments.map(a => a.offer.id));
    return allOffers.filter(o => !assignedOfferIds.has(o.id));
  };

  return (
    <div className="advertiser-view-page">
      <div className="view-header">
        <h1>Affiliate Details</h1>
        <div className="header-actions">
          <button className="btn btn-info" onClick={handleRefresh}>
            🔄 Refresh
          </button>
          <button className="btn btn-primary" onClick={handleEdit}>
            Edit
          </button>
          <button className="btn btn-warning" onClick={handleBack}>
            Back
          </button>
        </div>
      </div>

      <div className="view-form-container">
        <table className="advertiser-details-table">
          <tbody>
            <tr>
              <td className="form-label">ID</td>
              <td className="form-value">{affiliate.id}</td>
            </tr>

            <tr>
              <td className="form-label">Name</td>
              <td className="form-value">{affiliate.name}</td>
            </tr>

            <tr>
              <td className="form-label">Email</td>
              <td className="form-value">{affiliate.email}</td>
            </tr>

            <tr>
              <td className="form-label">Country</td>
              <td className="form-value">{affiliate.country}</td>
            </tr>

            <tr>
              <td className="form-label">Timezone</td>
              <td className="form-value">{affiliate.timezone}</td>
            </tr>

            <tr>
              <td className="form-label">Status</td>
              <td className="form-value">
                <span className={`status-badge ${affiliate.status}`}>
                  {affiliate.status.charAt(0).toUpperCase() + affiliate.status.slice(1)}
                </span>
              </td>
            </tr>

            <tr>
              <td className="form-label">Tier</td>
              <td className="form-value">
                <span className={`tier-badge ${affiliate.tier}`}>
                  {affiliate.tier.charAt(0).toUpperCase() + affiliate.tier.slice(1)}
                </span>
              </td>
            </tr>

            <tr>
              <td className="form-label">Postback Percentage</td>
              <td className="form-value">
                <span className="postback-percent" style={{ fontWeight: 'bold', color: '#3498db', fontSize: '15px' }}>
                  {affiliate.postbackPercentage}%
                </span>
              </td>
            </tr>

            <tr>
              <td className="form-label">Postback Status</td>
              <td className="form-value">
                <span className={`status-badge ${affiliate.postbackStatus === 'start' ? 'success' : 'warning'}`}>
                  {affiliate.postbackStatus === 'start' ? 'Active - Firing Postbacks' : 'Paused - No Postbacks'}
                </span>
              </td>
            </tr>

            <tr>
              <td className="form-label">Email Verified</td>
              <td className="form-value">
                <span className={`status-badge ${affiliate.emailVerified === 'YES' ? 'success' : 'warning'}`}>
                  {affiliate.emailVerified}
                </span>
              </td>
            </tr>

            <tr>
              <td className="form-label">Created</td>
              <td className="form-value">{affiliate.createdAt}</td>
            </tr>

            <tr>
              <td className="form-label">Last Login</td>
              <td className="form-value">{affiliate.lastLogin || 'Never'}</td>
            </tr>

            <tr>
              <td className="form-label">API URL</td>
              <td className="form-value">
                <code style={{ fontSize: '12px', backgroundColor: '#4a5568', padding: '4px 8px', borderRadius: '3px' }}>
                  {affiliate.apiUrl}
                </code>
              </td>
            </tr>

            <tr>
              <td className="form-label">API Key</td>
              <td className="form-value">
                <span style={{ fontFamily: 'monospace', backgroundColor: '#4a5568', padding: '4px 8px', borderRadius: '3px' }}>
                  {affiliate.apiKey}
                </span>
              </td>
            </tr>

            <tr>
              <td className="form-label">Login Username</td>
              <td className="form-value">
                <span style={{ fontFamily: 'monospace', backgroundColor: '#4a5568', padding: '4px 8px', borderRadius: '3px' }}>
                  {affiliate.username}
                </span>
              </td>
            </tr>

            <tr>
              <td className="form-label">Login Password</td>
              <td className="form-value">
                <span style={{ fontFamily: 'monospace', backgroundColor: '#4a5568', padding: '4px 8px', borderRadius: '3px' }}>
                  {affiliate.password}
                </span>
              </td>
            </tr>

            <tr>
              <td className="form-label">Postback URL</td>
              <td className="form-value">
                <code style={{ fontSize: '12px', backgroundColor: '#4a5568', padding: '4px 8px', borderRadius: '3px' }}>
                  {affiliate.postback}
                </code>
              </td>
            </tr>

            <tr>
              <td className="form-label">Daily Cap</td>
              <td className="form-value">{affiliate.dailyCap ? affiliate.dailyCap.toLocaleString() : 'Unlimited'}</td>
            </tr>

            <tr>
              <td className="form-label">Monthly Cap</td>
              <td className="form-value">{affiliate.monthlyCap ? affiliate.monthlyCap.toLocaleString() : 'Unlimited'}</td>
            </tr>

            <tr>
              <td className="form-label" style={{ verticalAlign: 'top', paddingTop: '20px' }}>Performance Stats</td>
              <td className="form-value">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', padding: '10px' }}>
                  <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ecf0f1' }}>{affiliate.totalClicks?.toLocaleString() || 0}</div>
                    <div style={{ fontSize: '12px', color: '#a0aec0' }}>Total Clicks</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ecf0f1' }}>{affiliate.totalConversions?.toLocaleString() || 0}</div>
                    <div style={{ fontSize: '12px', color: '#a0aec0' }}>Conversions</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ecf0f1' }}>{affiliate.conversionRate || '0.00'}%</div>
                    <div style={{ fontSize: '12px', color: '#a0aec0' }}>CVR</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ecf0f1' }}>${(parseFloat(String(affiliate.totalPayout || 0))).toFixed(2)}</div>
                    <div style={{ fontSize: '12px', color: '#a0aec0' }}>Total Payout</div>
                  </div>
                </div>
              </td>
            </tr>

            <tr>
              <td className="form-label" style={{ verticalAlign: 'top', paddingTop: '20px' }}>Assigned Offers</td>
              <td className="form-value">
                <div style={{ marginBottom: '15px' }}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowAssignModal(true)}
                    disabled={getAvailableOffers().length === 0}
                  >
                    ➕ Assign Offer
                  </button>
                </div>

                {assignments.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#a0aec0', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    No offers assigned to this affiliate yet.
                  </div>
                ) : (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#4a5568', position: 'sticky', top: 0 }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2d3748' }}>Offer</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2d3748' }}>Status</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2d3748' }}>Custom Payout</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2d3748' }}>Assigned Date</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #2d3748' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignments.map((assignment: any) => (
                          <tr key={assignment.id} style={{ borderBottom: '1px solid #4a5568' }}>
                            <td style={{ padding: '10px' }}>
                              <div style={{ fontWeight: 'bold' }}>{assignment.offer.name}</div>
                              <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                                {assignment.offer.advertiser?.name || 'N/A'}
                              </div>
                            </td>
                            <td style={{ padding: '10px' }}>
                              <span className={`status-badge ${assignment.status}`}>
                                {assignment.status.toUpperCase()}
                              </span>
                            </td>
                            <td style={{ padding: '10px' }}>
                              {assignment.custom_payout ? (
                                <span style={{ fontWeight: 'bold', color: '#27ae60' }}>
                                  ${assignment.custom_payout}
                                </span>
                              ) : (
                                <span style={{ color: '#a0aec0' }}>Default (${assignment.offer.payout})</span>
                              )}
                            </td>
                            <td style={{ padding: '10px', fontSize: '13px' }}>
                              {new Date(assignment.assigned_at).toLocaleDateString()}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleRemoveOffer(assignment.offer.id)}
                                style={{ fontSize: '12px' }}
                              >
                                ❌ Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Assign Offer Modal */}
      {showAssignModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#2d3748',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Assign Offer to Affiliate</h2>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Select Offer</label>
              <select
                value={selectedOfferId}
                onChange={(e) => setSelectedOfferId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#4a5568',
                  color: '#ecf0f1',
                  border: '1px solid #718096',
                  borderRadius: '4px'
                }}
              >
                <option value="">-- Select Offer --</option>
                {getAvailableOffers().map((offer: any) => (
                  <option key={offer.id} value={offer.id}>
                    {offer.name} - ${offer.payout} ({offer.advertiser?.name || 'N/A'})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                Custom Payout (optional)
              </label>
              <input
                type="number"
                step="0.01"
                value={customPayout}
                onChange={(e) => setCustomPayout(e.target.value)}
                placeholder="Leave empty to use offer default"
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#4a5568',
                  color: '#ecf0f1',
                  border: '1px solid #718096',
                  borderRadius: '4px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Notes (optional)</label>
              <textarea
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
                placeholder="Internal notes about this assignment..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#4a5568',
                  color: '#ecf0f1',
                  border: '1px solid #718096',
                  borderRadius: '4px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedOfferId('');
                  setCustomPayout('');
                  setAssignmentNotes('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAssignOffer}
                disabled={!selectedOfferId}
              >
                Assign Offer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewAffiliate;